import { useState, useEffect, useCallback, useRef } from "react";
import api from "../services/api";

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }); }

const LEGAL_FORMS = ["SAS", "SARL", "SA", "SCI", "SNC", "EURL", "Auto-entrepreneur", "EI", "Ltd", "GmbH", "BV", "Autre"];
const SECTORS = ["Commerce", "Industrie", "Services", "Construction", "Immobilier", "Finance", "Technologie", "Santé", "Agriculture", "Transport", "Autre"];
const COUNTRIES = ["France", "Allemagne", "Belgique", "Suisse", "Luxembourg", "Espagne", "Italie", "Royaume-Uni", "Pays-Bas", "Portugal", "États-Unis", "Canada", "Autre"];
// Répond au questionnaire d'onboarding (ActivityQuestionnaire.jsx) — modifiable ici, jamais figé à vie.
const ACTIVITY_TYPES = [
  { value: "services",     label: "Services" },
  { value: "marchandises", label: "Marchandises" },
  { value: "mixte",        label: "Mixte (services et marchandises)" },
];

const EMPTY = {
  code: "", name: "", siret: "", tva_number: "", address: "", city: "", sector: "",
  eori_number: "", rcs_number: "", naf_code: "", share_capital: "", legal_form: "",
  fiscal_year_start: "01/01", customs_code: "", country_of_establishment: "France",
  iban: "", bic: "", weight_unit_preference: "kg", activity_type: "", vat_exigibility: "debits", vat_accounts_by_rate: {}, sales_account_services: "", sales_account_biens: "",
};

function validateEori(eori) {
  if (!eori) return null;
  const val = eori.trim().toUpperCase();
  if (!/^[A-Z]{2}/.test(val)) return 'Doit commencer par un code pays (ex: FR)';
  if (val.startsWith('FR') && !/^FR\d{11}$/.test(val)) return 'EORI France : FR + 11 chiffres (ex: FR12345678901)';
  return null;
}

export default function Societes({ companies: initialCompanies, onCompaniesChange }) {
  const [companies, setCompanies] = useState(initialCompanies || []);
  const [loading, setLoading]     = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");
  const [eoriError, setEoriError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [activeSection, setActiveSection] = useState("general");

  // onCompaniesChange est recréé à chaque rendu d'App : en dépendre
  // relançait load() en boucle (GET /api/companies infini → 429 global).
  const onCompaniesChangeRef = useRef(onCompaniesChange);
  onCompaniesChangeRef.current = onCompaniesChange;

  const load = useCallback(() => {
    setLoading(true);
    api.companies.list()
      .then(data => { setCompanies(data); onCompaniesChangeRef.current?.(data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditTarget(null); setForm(EMPTY); setError(""); setEoriError("");
    setActiveSection("general"); setShowModal(true);
  };

  const openEdit = (c) => {
    setEditTarget(c);
    // Partir d'EMPTY : toutes les clés présentes → champs toujours contrôlés
    setForm({
      ...EMPTY,
      iban: c.iban || "", bic: c.bic || "",
      code: c.code, name: c.name, siret: c.siret || "", tva_number: c.tva_number || "",
      address: c.address || "", city: c.city || "", sector: c.sector || "",
      eori_number: c.eori_number || "", rcs_number: c.rcs_number || "",
      naf_code: c.naf_code || "", share_capital: c.share_capital || "",
      legal_form: c.legal_form || "", fiscal_year_start: c.fiscal_year_start || "01/01",
      customs_code: c.customs_code || "", country_of_establishment: c.country_of_establishment || "France",
      weight_unit_preference: c.weight_unit_preference || "kg",
      activity_type: c.activity_type || "",
      vat_exigibility: c.vat_exigibility || "debits",
      vat_exigibility_confirmed: false,
      // État réellement confirmé (valeur et date) : affichage seulement
      vat_exigibility_saved: c.vat_exigibility || "debits",
      vat_exigibility_confirmation: c.vat_exigibility_confirmation || null,
      vat_accounts_by_rate: (() => { try { return JSON.parse(c.vat_accounts_by_rate || "{}") || {}; } catch { return {}; } })(),
      sales_account_services: c.sales_account_services || "",
      sales_account_biens: c.sales_account_biens || "",
    });
    setError(""); setEoriError(""); setActiveSection("general"); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const eoriErr = validateEori(form.eori_number);
    if (eoriErr) { setEoriError(eoriErr); return; }
    setSaving(true); setError("");
    try {
      const payload = { ...form, share_capital: form.share_capital ? parseFloat(form.share_capital) : null };
      delete payload.vat_exigibility_saved; delete payload.vat_exigibility_confirmation; // affichage seulement
      if (editTarget) await api.companies.update(editTarget.id, payload);
      else await api.companies.create(payload);
      setShowModal(false); load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    await api.companies.delete(id).catch(console.error);
    setConfirmDelete(null); load();
  };

  const onEoriChange = (val) => {
    setForm(f => ({ ...f, eori_number: val.toUpperCase() }));
    setEoriError(validateEori(val) || "");
  };

  if (loading && companies.length === 0) {
    return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement…</div>;
  }

  const SECTIONS = [
    { id: "general",  label: "Général" },
    { id: "legal",    label: "Juridique" },
    { id: "customs",  label: "Douanes & EORI" },
  ];

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Gestion des sociétés</h2>
        <button className="btn primary" onClick={openCreate}>+ Nouvelle société</button>
      </div>

      <div className="kpi-grid k4">
        <div className="kpi"><div className="kpi-label">Sociétés actives</div><div className="kpi-value">{companies.length}</div></div>
        <div className="kpi"><div className="kpi-label">CA groupe consolidé</div><div className="kpi-value" style={{ color: "var(--g)" }}>{fmt(companies.reduce((s, c) => s + (c.ca_ytd || 0), 0))} €</div></div>
        <div className="kpi"><div className="kpi-label">Trésorerie groupe</div><div className="kpi-value">{fmt(companies.reduce((s, c) => s + (c.treso || 0), 0))} €</div></div>
        <div className="kpi"><div className="kpi-label">Score moyen</div><div className="kpi-value" style={{ color: "var(--g)" }}>{companies.length ? Math.round(companies.reduce((s, c) => s + (c.health_score || 85), 0) / companies.length) : "—"}/100</div></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 14 }}>
        {companies.map((c, i) => {
          const colors = ["#1a9e72", "#185fa5", "#ba7517", "#534ab7"];
          const color  = colors[i % colors.length];
          const score  = c.health_score || 85;
          const hasEori = !!c.eori_number;
          return (
            // Couleurs via les variables du thème : un fond #fff en dur avec le
            // texte du thème sombre (#F9FAFB) rendait SIRET/TVA/Trésorerie invisibles.
            <div key={c.id} style={{ padding: "16px", background: "var(--card-bg)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color, flexShrink: 0 }}>{c.code}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                    {c.name}
                    {hasEori && <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 8, background: "#1a9e7218", color: "#1a9e72", fontWeight: 700 }}>EORI ✓</span>}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    {c.legal_form && <span>{c.legal_form} · </span>}{c.sector || "—"}{c.city ? ` · ${c.city}` : ""}
                  </div>
                </div>
                <div className={`health-ring ${score >= 85 ? "green" : score >= 70 ? "amber" : "red"}`} style={{ width: 36, height: 36, fontSize: 11 }}>{score}</div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                <div style={{ background: "var(--bg)", borderRadius: 8, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10, color: "var(--text-muted)" }} title="Factures de vente validées HT de l'exercice, hors brouillons, avoirs déduits">CA facturé</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--g)" }}>{fmt(c.ca_facture)} €</div>
                </div>
                <div style={{ background: "var(--bg)", borderRadius: 8, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10, color: "var(--text-muted)" }}>Trésorerie</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{fmt(c.treso)} €</div>
                </div>
                <div style={{ background: "var(--bg)", borderRadius: 8, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10, color: "var(--text-muted)" }}>SIRET</div>
                  <div style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 600, color: "var(--text)" }}>{c.siret || "Non renseigné"}</div>
                </div>
                <div style={{ background: "var(--bg)", borderRadius: 8, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10, color: "var(--text-muted)" }}>N° TVA</div>
                  <div style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 600, color: "var(--text)" }}>{c.tva_number || "Non renseigné"}</div>
                </div>
              </div>

              {hasEori && (
                <div style={{ background: "#1a9e7210", border: "1px solid #1a9e7230", borderRadius: 8, padding: "6px 10px", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, color: "#1a9e72", fontWeight: 700 }}>EORI</span>
                  <span style={{ fontSize: 12, fontFamily: "monospace", color: "#1a9e72" }}>{c.eori_number}</span>
                  {c.customs_code && <><span style={{ color: "#ccc" }}>·</span><span style={{ fontSize: 11, color: "var(--text-muted)" }}>Code SH: {c.customs_code}</span></>}
                </div>
              )}

              {c.address && (
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 12 }}>📍 {c.address}{c.city ? `, ${c.city}` : ""}{c.country_of_establishment && c.country_of_establishment !== "France" ? ` (${c.country_of_establishment})` : ""}</div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn sm" style={{ flex: 1 }} onClick={() => openEdit(c)}>✏️ Modifier</button>
                <button className="btn sm" style={{ color: "var(--red)" }} onClick={() => setConfirmDelete(c)}>🗑 Supprimer</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal créer/modifier */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 620 }}>
            <div className="modal-header">
              <h3 className="modal-title">🏢 {editTarget ? `Modifier — ${editTarget.name}` : "Nouvelle société"}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>

            {/* Sous-onglets du formulaire */}
            <div style={{ display: "flex", gap: 2, borderBottom: "1px solid var(--border-color,#e2e8f0)", marginBottom: 18 }}>
              {SECTIONS.map(s => (
                <button key={s.id} type="button" onClick={() => setActiveSection(s.id)} style={{ padding: "6px 14px", fontSize: 12, fontWeight: activeSection === s.id ? 700 : 500, cursor: "pointer", border: "none", background: "transparent", color: activeSection === s.id ? "var(--g)" : "var(--text-muted)", borderBottom: `2px solid ${activeSection === s.id ? "var(--g)" : "transparent"}`, marginBottom: -1 }}>
                  {s.label}
                  {s.id === "customs" && (form.eori_number && !eoriError) && <span style={{ marginLeft: 4, color: "#1a9e72" }}>✓</span>}
                </button>
              ))}
            </div>

            {error && <div className="alert danger" style={{ marginBottom: 12 }}>{error}</div>}
            <form onSubmit={handleSubmit}>

              {/* Section Général */}
              {activeSection === "general" && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Code *</label>
                      <input className="form-control" required maxLength={6} placeholder="ex: MS" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} />
                    </div>
                    <div className="form-group" style={{ flex: 3 }}>
                      <label className="form-label">Nom de la société *</label>
                      <input className="form-control" required placeholder="MaSociété SAS" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">SIRET</label>
                      <input className="form-control" placeholder="12345678901234" maxLength={14} value={form.siret} onChange={e => setForm(f => ({ ...f, siret: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">N° TVA intracommunautaire</label>
                      <input className="form-control" placeholder="FR12345678901" value={form.tva_number} onChange={e => setForm(f => ({ ...f, tva_number: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">IBAN</label>
                      <input className="form-control" placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" value={form.iban} onChange={e => setForm(f => ({ ...f, iban: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">BIC / SWIFT</label>
                      <input className="form-control" placeholder="BNPAFRPPXXX" maxLength={11} value={form.bic} onChange={e => setForm(f => ({ ...f, bic: e.target.value.toUpperCase() }))} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Adresse</label>
                    <input className="form-control" placeholder="12 rue de la Paix" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Ville</label>
                      <input className="form-control" placeholder="Paris" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Secteur d'activité</label>
                      <select className="form-control" value={form.sector} onChange={e => setForm(f => ({ ...f, sector: e.target.value }))}>
                        <option value="">— Choisir —</option>
                        {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Type d'activité</label>
                      <select className="form-control" value={form.activity_type} onChange={e => setForm(f => ({ ...f, activity_type: e.target.value }))}>
                        <option value="">— Non renseigné —</option>
                        {ACTIVITY_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Exigibilité de la TVA</label>
                      <select className="form-control" value={form.vat_exigibility} onChange={e => setForm(f => ({ ...f, vat_exigibility: e.target.value }))}>
                        <option value="debits">Sur les débits (à la facturation)</option>
                        <option value="encaissements">Sur les encaissements (prestations de services)</option>
                      </select>
                      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                        Sur les encaissements : la TVA des prestations n'est déclarée qu'une fois la facture payée (règle par défaut des prestations de services, sauf option pour les débits). Les ventes de biens restent sur les débits. S'applique aux factures validées après le changement.
                      </div>
                      {editTarget && (
                        <details style={{ marginTop: 10 }}>
                          <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Comptes comptables des ventes (facultatif)</summary>
                          <div style={{ fontSize: 11, color: "#6b7280", margin: "4px 0 6px" }}>
                            Laissés vides : TVA collectée en 44571 et compte de vente actuel (aucun changement). À renseigner pour suivre le plan de comptes de votre expert-comptable ; s'applique aux factures validées après l'enregistrement.
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 6 }}>
                            {["20", "10", "5.5", "2.1"].map(rate => (
                              <label key={rate} style={{ fontSize: 11 }}>TVA collectée {rate.replace(".", ",")} %
                                <input className="form-control" placeholder="44571" value={form.vat_accounts_by_rate?.[rate] || ""}
                                  onChange={e => setForm(f => ({ ...f, vat_accounts_by_rate: { ...(f.vat_accounts_by_rate || {}), [rate]: e.target.value.trim() } }))} />
                              </label>
                            ))}
                            <label style={{ fontSize: 11 }}>Ventes — prestations
                              <input className="form-control" placeholder="compte actuel" value={form.sales_account_services} onChange={e => setForm(f => ({ ...f, sales_account_services: e.target.value.trim() }))} />
                            </label>
                            <label style={{ fontSize: 11 }}>Ventes — biens
                              <input className="form-control" placeholder="compte actuel" value={form.sales_account_biens} onChange={e => setForm(f => ({ ...f, sales_account_biens: e.target.value.trim() }))} />
                            </label>
                          </div>
                        </details>
                      )}
                      {/* Confirmation (28/10/2026) : vaut pour la valeur confirmée ; tout changement en demande une nouvelle */}
                      {editTarget && (() => {
                        const c = form.vat_exigibility_confirmation;
                        const frDate = (d) => new Date(String(d).replace(" ", "T") + "Z").toLocaleDateString("fr-FR");
                        const label = (v) => (v === "encaissements" ? "Sur les encaissements" : "Sur les débits");
                        const stillConfirmed = c?.confirmed && form.vat_exigibility === form.vat_exigibility_saved;
                        return stillConfirmed ? (
                          <div style={{ fontSize: 11, color: "#1a9e72", marginTop: 4 }}>✓ Réglage confirmé : {label(c.confirmed_value)}, le {frDate(c.confirmed_at)}</div>
                        ) : (
                          <>
                            {c?.confirmed_at && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>Dernière confirmation : {label(c.confirmed_value)}, le {frDate(c.confirmed_at)}{form.vat_exigibility !== c.confirmed_value ? " — le réglage a changé depuis" : ""}.</div>}
                            <label style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 12, color: "#b45309", marginTop: 6 }}>
                              <input type="checkbox" checked={form.vat_exigibility_confirmed} onChange={e => setForm(f => ({ ...f, vat_exigibility_confirmed: e.target.checked }))} />
                              <span>Je confirme ce réglage : {label(form.vat_exigibility)}. Tant qu'il n'est pas confirmé, un avertissement s'affiche à chaque validation de facture.</span>
                            </label>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Unité de poids par défaut</label>
                      <select className="form-control" value={form.weight_unit_preference} onChange={e => setForm(f => ({ ...f, weight_unit_preference: e.target.value }))}>
                        <option value="kg">Kilogrammes (kg)</option>
                        <option value="g">Grammes (g)</option>
                      </select>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                        Affichage et saisie du poids sur les fiches produits. Le poids reste toujours stocké en kg en interne — réversible à tout moment.
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Section Juridique */}
              {activeSection === "legal" && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Forme juridique</label>
                      <select className="form-control" value={form.legal_form} onChange={e => setForm(f => ({ ...f, legal_form: e.target.value }))}>
                        <option value="">— Choisir —</option>
                        {LEGAL_FORMS.map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Capital social (€)</label>
                      <input className="form-control" type="number" step="1" min="0" placeholder="10000" value={form.share_capital} onChange={e => setForm(f => ({ ...f, share_capital: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">N° RCS</label>
                      <input className="form-control" placeholder="Paris B 123 456 789" value={form.rcs_number} onChange={e => setForm(f => ({ ...f, rcs_number: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Code APE / NAF</label>
                      <input className="form-control" placeholder="6201Z" maxLength={6} value={form.naf_code} onChange={e => setForm(f => ({ ...f, naf_code: e.target.value.toUpperCase() }))} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Début d'exercice fiscal</label>
                      <input className="form-control" placeholder="01/01 ou 01/07" maxLength={5} value={form.fiscal_year_start} onChange={e => setForm(f => ({ ...f, fiscal_year_start: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Pays d'établissement</label>
                      <select className="form-control" value={form.country_of_establishment} onChange={e => setForm(f => ({ ...f, country_of_establishment: e.target.value }))}>
                        {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {/* Section Douanes & EORI */}
              {activeSection === "customs" && (
                <>
                  <div style={{ padding: "10px 14px", background: "#185fa510", border: "1px solid #185fa530", borderRadius: 8, marginBottom: 16, fontSize: 12, color: "#185fa5" }}>
                    <strong>Numéro EORI</strong> (Economic Operators Registration and Identification) — Obligatoire pour les opérations douanières dans l'UE. Format France : <code>FR</code> + 11 chiffres.
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Numéro EORI</label>
                      <input className="form-control" placeholder="FR12345678901" value={form.eori_number}
                        onChange={e => onEoriChange(e.target.value)}
                        style={{ borderColor: eoriError ? "var(--red)" : undefined, fontFamily: "monospace" }} />
                      {eoriError && <div style={{ fontSize: 11, color: "var(--red)", marginTop: 4 }}>{eoriError}</div>}
                      {form.eori_number && !eoriError && <div style={{ fontSize: 11, color: "#1a9e72", marginTop: 4 }}>✓ Format EORI valide</div>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Code douanier principal (SH)</label>
                      <input className="form-control" placeholder="8471.30 (ex: ordinateurs)" value={form.customs_code} onChange={e => setForm(f => ({ ...f, customs_code: e.target.value }))} />
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Code SH du produit principal exporté</div>
                    </div>
                  </div>
                  <div style={{ padding: "10px 14px", background: "#f9fafb", border: "var(--border)", borderRadius: 8, marginTop: 8, fontSize: 12 }}>
                    <div style={{ fontWeight: 700, marginBottom: 8, color: "var(--text-muted)" }}>Informations automatiquement incluses sur les factures export :</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                      {[
                        ["Forme juridique", form.legal_form || "—"],
                        ["Capital social", form.share_capital ? `${fmt(form.share_capital)} €` : "—"],
                        ["RCS", form.rcs_number || "—"],
                        ["NAF/APE", form.naf_code || "—"],
                        ["EORI", form.eori_number || "—"],
                        ["Code SH", form.customs_code || "—"],
                      ].map(([k, v]) => (
                        <div key={k}><span style={{ color: "var(--text-muted)" }}>{k} : </span><strong>{v}</strong></div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="btn-group" style={{ justifyContent: "flex-end", marginTop: 20 }}>
                <button type="button" className="btn" onClick={() => setShowModal(false)}>Annuler</button>
                {/* Keys distinctes : « Suivant » et l'enregistrement restent deux
                    boutons séparés (jamais un même <button> qui change de type). */}
                {activeSection !== "customs" && (
                  <button key="next" type="button" className={editTarget ? "btn" : "btn primary"} onClick={() => {
                    const idx = SECTIONS.findIndex(s => s.id === activeSection);
                    setActiveSection(SECTIONS[idx + 1]?.id || "customs");
                  }}>Suivant →</button>
                )}
                {/* En modification : enregistrable depuis n'importe quel onglet */}
                {(editTarget || activeSection === "customs") && (
                  <button key="submit" type="submit" className="btn primary" disabled={saving || !!eoriError}>
                    {saving ? "Enregistrement…" : editTarget ? "Enregistrer" : "Créer la société"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">⚠️ Confirmer la suppression</h3></div>
            <p style={{ fontSize: 13, marginBottom: 16 }}>
              Voulez-vous désactiver la société <strong>{confirmDelete.name}</strong> ? Les données associées seront conservées.
            </p>
            <div className="btn-group" style={{ justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setConfirmDelete(null)}>Annuler</button>
              <button className="btn" style={{ background: "var(--red)", color: "#fff" }} onClick={() => handleDelete(confirmDelete.id)}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
